import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { Link } from "react-router-dom"; // Import Link
import { Phone, Mail, UserPlus, MapPin, Instagram, Facebook } from "lucide-react";
import logoAnchor from "@/assets/Favicon.png";
import WorkWithUsDialog from "@/components/WorkWithUsDialog"; // Import the new component

const WhatsappIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" {...props}>
    <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
  </svg>
);


const Footer = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(footerRef, { once: true, amount: 0.1 });
  const [isDialogOpen, setIsDialogOpen] = useState(false); // State to control dialog visibility

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end end"],
  });

  // Anchor slides down as user scrolls to footer
  const anchorY = useTransform(scrollYProgress, [0, 0.8, 1], [-200, 0, 0]);
  const anchorOpacity = useTransform(scrollYProgress, [0, 0.5, 0.8], [0, 0.5, 1]);
  const anchorScale = useTransform(scrollYProgress, [0.8, 1], [0.9, 1]);

  // Chain grows as you scroll
  const chainHeight = useTransform(scrollYProgress, [0, 0.8], ["0%", "100%"]);



  const contactInfo = [
    { type: "email", label: "E-mail Corporativo", value: "jldespachantenautico@gmail.com" },
  ];

  const addresses = [
    {
      base: "Base Balneário Camboriú",
      address: "R. Dom Henrique, 1200 - Jardim Iate Clube",
      city_state: "Balneário Camboriú - SC, 88337-260",
      mapUrl: "https://www.google.com/maps/place/JL+Despachante+e+Escola+N%C3%A1utica/@-27.0152993,-48.6348353,17z/data=!3m1!4b1!4m6!3m5!1s0x94d8b71d5bf80a2b:0x3c520e49d7133a!8m2!3d-27.0153041!4d-48.6322604!16s%2Fg%2F11m_832m_1?entry=ttu&g_ep=EgoyMDI2MDIxMC4wIKXMDSoASAFQAw%3D%3D"
    },
    {
      base: "Base São Francisco do Sul",
      address: "Lgo. Almirante Moraes Rego, 156 - Centro",
      city_state: "São Francisco do Sul - SC, 89240-000",
      mapUrl: "https://www.google.com/maps/place/JL+Despachante+e+Escola+N%C3%A1utica/@-26.2454643,-48.6399038,17z/data=!3m1!4b1!4m6!3m5!1s0x94d94f32b9b822f9:0xf9d3ce532e7d2b85!8m2!3d-26.2454643!4d-48.6399038!16s%2Fg%2F11h_yv5hn7?entry=ttu&g_ep=EgoyMDI2MDIxMC4wIKXMDSoASAFQAw%3D%3D"
    },
    {
      base: "Base Barra do Sul",
      address: "Av. Amândio Cabral, 491 - Centro",
      city_state: "Balneário Barra do Sul - SC, 89247-000",
      mapUrl: "https://maps.app.goo.gl/xvJjBmxCJnwjdX3KA"
    },
    {
      base: "Base Canoinhas",
      address: "Rua Irmã Maria Felicitas, 631 - Centro",
      city_state: "Canoinhas - SC",
      mapUrl: "https://www.google.com/maps/search/?api=1&query=Rua+Irm%C3%A3+Maria+Felicitas%2C+631%2C+Centro%2C+Canoinhas+-+SC"
    },
  ];

  return (
    <footer ref={containerRef} className="relative" style={{ backgroundColor: 'hsl(222, 84%, 5%)' }}>
      {/* Chain Animation Container */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full z-20 flex flex-col items-center pointer-events-none">
        {/* Chain */}
        <motion.div
          style={{ height: chainHeight }}
          className="w-1 bg-gradient-to-b from-transparent via-accent/50 to-accent overflow-hidden"
        >
          {/* Chain Links Pattern */}
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `repeating-linear-gradient(
                to bottom,
                transparent 0px,
                transparent 8px,
                hsl(43, 74%, 43%) 8px,
                hsl(43, 74%, 43%) 12px
              )`,
            }}
          />
        </motion.div>

        {/* Anchor */}
        <motion.div
          style={{ y: anchorY, opacity: anchorOpacity, scale: anchorScale }}
          className="relative"
        >
          <img
            src={logoAnchor}
            alt="JL Escola Náutica Logo"
            className="w-20 h-auto drop-shadow-2xl"
            style={{
              filter: 'drop-shadow(0 10px 30px rgba(212, 175, 55, 0.3))',
            }}
          />
        </motion.div>
      </div>

      {/* Footer Content */}
      <div ref={footerRef} className="relative z-10 pt-32 pb-12">
        <div className="container mx-auto px-6 lg:px-12">
          {/* Top Section */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {} as any}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-16"
          >
            {/* Brand Column */}
            <div className="col-span-1">
              <div className="flex items-center gap-3 mb-6">
                <img src={logoAnchor} alt="JL Escola Náutica Logo" className="h-10 w-auto" />
                <div>
                  <span className="font-heading text-lg font-semibold text-primary-foreground">
                    JL Escola Náutica
                  </span>
                  <p className="text-xs text-primary-foreground/50">Escola Náutica</p>
                </div>
              </div>
              <p className="text-sm text-primary-foreground/60 leading-relaxed mb-6">
                Referência em formação náutica e assessoria documental para embarcações
                em Santa Catarina, com atendimento personalizado e excelência em cada detalhe.
              </p>
              {/* Social Links */}
              <div className="flex items-center gap-4">
                {[
                  { Icon: Instagram, href: "https://www.instagram.com/jlescolanautica/", label: "Instagram" },
                  { Icon: Facebook, href: "https://www.facebook.com/jlescolanautica/", label: "Facebook" },
                  { Icon: WhatsappIcon, href: "https://wa.me/554792691561", label: "WhatsApp" },
                ].map(({ Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    className="w-10 h-10 rounded-full bg-transparent flex items-center justify-center text-gold hover:bg-gold/10 transition-colors duration-300 border border-gold/50"
                    aria-label={label}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div>
              <h4 className="font-heading text-base font-semibold text-gold mb-4 uppercase tracking-wider">
                Navegação
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/" className="text-sm text-primary-foreground/60 hover:text-gold transition-colors duration-200">
                    Início
                  </Link>
                </li>
                <li>
                  <Link to="/#servicos" className="text-sm text-primary-foreground/60 hover:text-gold transition-colors duration-200">
                    Serviços
                  </Link>
                </li>
                <li>
                  <Link to="/#unidades" className="text-sm text-primary-foreground/60 hover:text-gold transition-colors duration-200">
                    Unidades
                  </Link>
                </li>
                <li>
                  <Link to="/#diferenciais" className="text-sm text-primary-foreground/60 hover:text-gold transition-colors duration-200">
                    Sobre Nós
                  </Link>
                </li>
              </ul>
            </div>

            {/* Fale Conosco */}
            <div>
              <h4 className="font-heading text-base font-semibold text-gold mb-4 uppercase tracking-wider">
                Fale Conosco
              </h4>
              <div className="space-y-4">
                {contactInfo.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 text-sm text-primary-foreground/60">
                    {item.type === "phone" ? (
                      <Phone className="w-4 h-4 text-accent" />
                    ) : (
                      <Mail className="w-4 h-4 text-accent" />
                    )}
                    <div>
                      <p className="font-medium text-primary-foreground/80">{item.label}</p>
                      <p>{item.value}</p>
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => setIsDialogOpen(true)}
                  className="mt-6 inline-flex items-center gap-2 px-4 py-2 border border-accent text-accent rounded-md hover:bg-accent hover:text-primary-foreground transition-colors duration-300"
                >
                  <UserPlus className="w-4 h-4" />
                  Trabalhe Conosco
                </button>
              </div>
            </div>

            {/* Nossas Unidades */}
            <div>
              <h4 className="font-heading text-base font-semibold text-gold mb-4 uppercase tracking-wider">
                Nossas Unidades
              </h4>
              <div className="space-y-4">
                {addresses.map((addr, index) => (
                  <div key={index} className="flex items-start gap-3 text-sm text-primary-foreground/60">
                    <MapPin className="w-4 h-4 text-accent mt-1" />
                    <div>
                      <p className="font-medium text-primary-foreground/80">
                        <a href={(addr as any).mapUrl} target="_blank" rel="noopener noreferrer" className="hover:underline text-gold">
                          {addr.base}
                        </a>
                      </p>
                      <p>{addr.address}</p>
                      <p>{addr.city_state}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Divider */}
          <div className="w-full h-px bg-gradient-to-r from-transparent via-primary-foreground/20 to-transparent mb-8" />

          {/* Bottom Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {} as any}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col md:flex-row items-center justify-between gap-4"
          >
            <p className="text-xs text-primary-foreground/40">
              © 2026 JL Escola Náutica. Todos os direitos reservados.
            </p>
            <div className="flex items-center gap-6">
              <p className="text-xs text-primary-foreground/40">
                CNPJ: 38.391.150/0001-46
              </p>
              <a href="#" className="text-xs text-primary-foreground/40 hover:text-accent transition-colors">
                Política de Privacidade
              </a>
            </div>
          </motion.div>
        </div>
      </div>
      <WorkWithUsDialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)} />
    </footer>
  );
};

export default Footer;
